<script setup lang="ts">
type Props = {
  opacity?: boolean;
  allowGrow?: boolean;
  // rótulo alternativo do botão de cancelar edição (Minha Conta V2 usa "Voltar")
  cancelLabel?: string;
};

const {
  opacity = false,
  allowGrow = false,
  cancelLabel = '',
} = defineProps<Props>();

const route = useRoute();
const { t } = useTexts();
const { openToaster } = useLayoutStore();
const { fetchUserProfile } = useUserData();
const { updateUserContract } = useUserDataStore();
const { isUserMigrationEnabled, userMigrationNewDomain } = useFeatureFlagsNew();
const { getDateTime } = useDateTimeHelper();

const {
  termsAndConditionsAcceptedDate,
  privacyPolicyAcceptedDate,
  lgpdAcceptedDate,
  lawAcceptedDate,
  migrationAcceptedDate,
} = useUserValidations();

const isTermsAndConditionsAccepted = computed<boolean>(() => {
  return !!termsAndConditionsAcceptedDate.value;
});

const isPrivacyPolicyAccepted = computed<boolean>(() => {
  return !!privacyPolicyAcceptedDate.value;
});

const isLgpdAccepted = computed<boolean>(() => {
  return !!lgpdAcceptedDate.value;
});

const isLawAccepted = computed<boolean>(() => {
  return !!lawAcceptedDate.value;
});

const isMigrationAccepted = computed<boolean>(() => {
  if (!isUserMigrationEnabled.value) {
    return true;
  }

  return !!migrationAcceptedDate.value;
});

const isLoading = ref<boolean>(false);
const isEditing = ref<boolean>(false);

async function updateContractStatus(type: string) {
  if (
    !type ||
    (type === 'terms' && isTermsAndConditionsAccepted.value) ||
    (type === 'privacy' && isPrivacyPolicyAccepted.value) ||
    (type === 'lgpd' && isLgpdAccepted.value) ||
    (type === 'law' && isLawAccepted.value) ||
    (type === 'migration' && isMigrationAccepted.value)
  ) {
    return;
  }

  isLoading.value = true;

  await updateUserContract(type)
    .then(async (res) => {
      const message =
        res?.data?.message || t('account.contracts_section.update_success');

      openToaster({
        message,
        type: 'success',
      });

      await fetchUserProfile({ force: true });

      isLoading.value = false;

      if (!hasPendency.value) {
        isEditing.value = false;
      }
    })
    .catch((err) => {
      const errorData = err?.data?.message;

      const errorMessage =
        errorData || t('account.contracts_section.update_error');

      openToaster({
        message: errorMessage,
        type: 'error',
      });

      isLoading.value = false;
    });
}

const contractsList = computed(() => {
  return [
    {
      type: 'terms',
      title: 'Termos e Condições',
      link: '/page/terms',
      status: isTermsAndConditionsAccepted.value,
      date: termsAndConditionsAcceptedDate.value,
    },
    {
      type: 'privacy',
      title: 'Política de Privacidade',
      link: '/page/privacy',
      status: isPrivacyPolicyAccepted.value,
      date: privacyPolicyAcceptedDate.value,
    },
    {
      type: 'lgpd',
      title: 'LGPD e Cookies',
      link: '/page/lgpd',
      status: isLgpdAccepted.value,
      date: lgpdAcceptedDate.value,
    },
    {
      type: 'law',
      title: 'Declaro que meus dados são verdadeiros',
      status: isLawAccepted.value,
      date: lawAcceptedDate.value,
    },
    ...(isUserMigrationEnabled.value
      ? [
          {
            type: 'migration',
            title: `Aceito migrar minha conta para a ${userMigrationNewDomain.value}`,
            status: isMigrationAccepted.value,
            date: migrationAcceptedDate.value,
          },
        ]
      : []),
  ];
});

const hasPendency = computed(() => {
  return (
    !isTermsAndConditionsAccepted.value ||
    !isPrivacyPolicyAccepted.value ||
    !isLgpdAccepted.value ||
    !isLawAccepted.value ||
    !isMigrationAccepted.value
  );
});

const toggleEdit = () => {
  isEditing.value = !isEditing.value;
};

defineExpose({
  name: 'contracts',
  isEditing,
  hasPendency,
  toggleEdit,
});

onMounted(() => {
  if (route.query?.highlight === 'contracts' && hasPendency.value) {
    isEditing.value = true;
  }
});
</script>

<template>
  <ClientOnly>
    <BaseLoading
      class="!bg-auth-bg-primary !absolute !z-20"
      :icon-loader="true"
      :show="isLoading"
    />

    <div
      :class="[
        $style.container,
        { [$style.opacity]: opacity, [$style.grow]: allowGrow && !isEditing },
      ]"
    >
      <UserProfileDataCard
        :action-label="
          isEditing ? cancelLabel || $t('general.cancel') : $t('general.view')
        "
        :action-type="!isEditing ? 'view' : undefined"
        :badge-label="
          hasPendency ? $t('account.pending') : $t('account.complete')
        "
        :badge-type="hasPendency ? 'error' : 'success'"
        :label="$t('account.contracts_section.contracts')"
        @action="toggleEdit"
      >
        {{ $t('account.contracts_section.summary') }}
        <button
          v-if="hasPendency && !isEditing"
          :class="$style.pendingBtn"
          @click="toggleEdit"
        >
          {{ $t('account.contracts_section.pendencies') }}
        </button>
      </UserProfileDataCard>

      <div
        v-if="isEditing"
        :class="$style.editContent"
      >
        <div :class="$style.description">
          {{
            $t('account.contracts_section.description', { appName: appName() })
          }}
        </div>

        <ul :class="$style.contractsList">
          <li
            v-for="contract in contractsList"
            :key="contract.title"
            :class="$style.contractItem"
          >
            <span>
              <BaseFormToggleStatic
                :class="$style.switch"
                :disabled="contract.status"
                :status="contract.status"
                @click="updateContractStatus(contract.type)"
              />
            </span>
            <div :class="$style.contentContracts">
              <span>
                <strong>{{ contract.title }}</strong>
              </span>
              <span :class="$style.pipe"> | </span>
              <span
                v-if="contract.status"
                :class="$style.date"
              >
                {{ $t('account.contracts_section.accepted_at') }}
                <strong>{{ getDateTime(contract.date) }}</strong>
              </span>
              <span
                v-else
                :class="$style.pending"
              >
                <NuxtIcon name="close" />
                {{ $t('account.pending') }}
              </span>
              <span
                v-if="contract.link"
                :class="$style.link"
              >
                <NuxtLink
                  target="_blank"
                  :to="contract.link"
                >
                  {{ contract.title }}
                  <NuxtIcon name="external" />
                </NuxtLink>
              </span>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </ClientOnly>
</template>

<style module lang="scss">
.container {
  @apply flex flex-col gap-3 w-full;
}

.grow {
  --card-grow: 1;
}

.opacity {
  @apply opacity-20 pointer-events-none select-none;
}

.pendingBtn {
  @apply block text-xs text-primary mt-1 text-left;
}

.editContent {
  @apply w-full;

  .description {
    @apply text-sm md:text-sm text-texts/70 mb-4 max-w-full;
  }

  .contractsList {
    @apply list-none flex flex-col gap-2;

    .contractItem {
      @apply flex items-center gap-4 md:gap-3 py-2 md:py-4 px-4 bg-texts/5 rounded-lg;

      span {
        @apply text-xs md:text-sm text-texts/90;

        &.pending {
          @apply text-error text-2xs md:text-xs font-medium flex items-center gap-1;

          svg {
            @apply text-error text-2xs;
          }
        }
      }

      .contentContracts {
        @apply flex flex-col md:flex-row items-start md:items-center gap-0.5 md:gap-2 flex-1;

        .pipe {
          @apply hidden md:block text-texts/50 text-xs;
        }

        .date {
          @apply text-2xs md:text-xs text-texts/50;
        }

        .link {
          @apply text-2xs md:text-xs ml-0 md:ml-auto;
        }

        strong {
          @apply font-semibold;
        }

        a {
          @apply text-primary text-2xs cursor-pointer flex items-center gap-1.5;

          svg {
            @apply text-primary text-[.6rem];
          }
        }
      }
    }
  }

  .btnSubmit {
    @apply bg-button-bg text-button-text py-2 px-4 rounded-md text-sm font-semibold whitespace-nowrap flex items-center justify-center gap-2;

    &[disabled]:not([disabled='false']) {
      @apply opacity-40 cursor-not-allowed;
    }
  }
}
</style>
